import React, { useEffect, useState, useContext, useRef } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  Typography,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import {
  Close,
  PhonelinkRing,
  CropFree,
} from "@material-ui/icons";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { detectConnector, runAssertion } from "../../utils/passkeyBridge";

const QR_TIMEOUT = 60;

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    overflow: "hidden",
    maxWidth: 420,
    width: "100%",
    margin: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  header: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    padding: "20px 24px 16px",
    color: "#fff",
    position: "relative",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTexts: {
    flex: 1,
  },
  closeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    color: "rgba(255,255,255,0.8)",
    "&:hover": {
      color: "#fff",
      backgroundColor: "rgba(255,255,255,0.15)",
    },
  },
  body: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#fafafa",
  },
  qrWrapper: {
    position: "relative",
    padding: 16,
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: `3px solid ${theme.palette.primary.main}`,
  },
  qrOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 2px 8px ${theme.palette.primary.main}66`,
  },
  timerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 20,
    gap: 8,
  },
  timerCircle: {
    position: "relative",
    display: "inline-flex",
  },
  timerText: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  steps: {
    marginTop: 20,
    width: "100%",
  },
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "8px 0",
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: theme.palette.type === "dark" ? "#333" : `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 2,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 0",
    gap: 16,
  },
  loadingIcon: {
    animation: "$spin 2s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  footer: {
    padding: "12px 24px",
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#fafafa",
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [countdown, setCountdown] = useState(QR_TIMEOUT);
  const [passkeyRequested, setPasskeyRequested] = useState(null);
  const [extensionMissing, setExtensionMissing] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
        setPasskeyRequested(null);
        setExtensionMissing(false);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!qrCode || !open) return;
    setCountdown(QR_TIMEOUT);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [qrCode, open]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = user.companyId;
    const onWhatsappData = async (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }
      if (data.action === "passkeyRequired" && data.session.id === whatsAppId) {
        setPasskeyRequested(data.passkeyOptions);
        
        const hasExtension = await detectConnector();
        if (hasExtension) {
           try {
              await runAssertion(whatsAppId, data.passkeyOptions);
           } catch (err) {
              toastError(err);
           }
        } else {
           setExtensionMissing(true);
        }
      }
      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);
    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, user.companyId, socket]);

  const timerColor = countdown <= 10 ? "#d32f2f" : countdown <= 20 ? "#ff9800" : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="qr-code-dialog"
    >
      <div className={classes.header}>
        <IconButton className={classes.closeBtn} onClick={onClose} size="small">
          <Close fontSize="small" />
        </IconButton>
        <div className={classes.headerContent}>
          <div className={classes.headerIcon}>
            <PhonelinkRing style={{ fontSize: 24 }} />
          </div>
          <div className={classes.headerTexts}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.2 }}>
              Conectar WhatsApp
            </Typography>
            <Typography variant="caption" style={{ opacity: 0.85 }}>
              Escaneie o código com seu celular
            </Typography>
          </div>
        </div>
      </div>

      <div className={classes.body}>
        {extensionMissing ? (
          <div className={classes.loadingContainer} style={{textAlign: "center"}}>
            <Typography variant="h6" color="error">
              Extensão Necessária
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
              Sua conta exige o uso de Passkey (Biometria). Você precisa instalar nossa extensão de segurança para conectar.
            </Typography>
            <a href="/wa-passkey-connector.zip" download style={{marginTop: 15, padding: "10px 20px", background: "#d32f2f", color: "#fff", textDecoration: "none", borderRadius: 8}}>
              Baixar Extensão
            </a>
          </div>
        ) : passkeyRequested ? (
          <div className={classes.loadingContainer} style={{textAlign: "center"}}>
            <CropFree className={classes.loadingIcon} style={{ fontSize: 48 }} color="primary" />
            <Typography variant="h6" color="primary">
              Aguardando Passkey
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
              O WhatsApp solicitou Passkey. Confirme usando a biometria/senha no seu dispositivo.
            </Typography>
          </div>
        ) : qrCode ? (
          <>
            <div className={classes.qrWrapper}>
              <QRCode value={qrCode} size={240} level="M" style={{ display: "block" }} renderAs="svg" />
              <div className={classes.qrOverlay}>
                <CropFree style={{ color: "#fff", fontSize: 24 }} />
              </div>
            </div>

            <div className={classes.timerContainer}>
              <div className={classes.timerCircle}>
                <CircularProgress
                  variant="determinate"
                  value={(countdown / QR_TIMEOUT) * 100}
                  size={40}
                  thickness={3}
                  color={timerColor ? undefined : "primary"}
                  style={timerColor ? { color: timerColor } : undefined}
                />
                <div className={classes.timerText}>
                  <Typography
                    variant="caption"
                    color={timerColor ? undefined : "primary"}
                    style={{ fontWeight: 700, fontSize: 13, ...(timerColor ? { color: timerColor } : {}) }}
                  >
                    {countdown}s
                  </Typography>
                </div>
              </div>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                {countdown > 0 ? "QR Code expira em breve" : "QR Code expirado, aguarde um novo"}
              </Typography>
            </div>

            <div className={classes.steps}>
              <div className={classes.step}>
                <div className={classes.stepNumber}>1</div>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                  Abra o WhatsApp no seu celular
                </Typography>
              </div>
              <div className={classes.step}>
                <div className={classes.stepNumber}>2</div>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                  Toque em <strong>Mais opções</strong> ou <strong>Configurações</strong> e selecione <strong>Aparelhos conectados</strong>
                </Typography>
              </div>
              <div className={classes.step}>
                <div className={classes.stepNumber}>3</div>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                  Aponte seu celular para esta tela para escanear o código
                </Typography>
              </div>
              <Typography variant="body2" color="error" style={{ fontSize: 12, marginTop: 10, textAlign: 'center', fontWeight: 'bold' }}>
                <span role="img" aria-label="warning emoji">⚠️</span> O QR Code não deve ser lido pela câmera comum do celular, mas sim pelo aplicativo WhatsApp.
              </Typography>
            </div>
          </>
        ) : (
          <div className={classes.loadingContainer}>
            <CropFree className={classes.loadingIcon} style={{ fontSize: 48 }} color="primary" />
            <Typography variant="body1" color="textSecondary">
              Gerando QR Code...
            </Typography>
            <CircularProgress size={24} color="primary" />
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
          <span role="img" aria-label="lock emoji">🔒</span> Conexão criptografada de ponta a ponta
        </Typography>
      </div>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
